import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button as ChakraButton,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  Document,
  Image,
  Page,
  pdf,
  StyleSheet,
  Text as TextPDF,
  View,
} from '@react-pdf/renderer';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IoAddCircleSharp,
  IoDownloadOutline,
  IoLogoWhatsapp,
  IoPencilOutline,
  IoPrintOutline,
  IoSearchSharp,
  IoTrashOutline,
} from 'react-icons/io5';
import { FiMail, FiMapPin, FiUsers } from 'react-icons/fi';
import { MdOutlineTaskAlt } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Form/Button';
import HeaderSideBar from '../components/HeaderSideBar';
import Pagination from '../components/Pagination';
import { useAuth } from '../contexts/AuthContext';
import { VoterDTO } from '../dtos';
import { useDebounce } from '../hooks/useDebounce';
import api from '../services/api';
import { convertDateFormat } from '../utils/convertDateFormat';
import { getFormatDate } from '../utils/date';
import * as XLSX from 'xlsx';

type VoterFilterType =
  | 'all'
  | 'name'
  | 'reference'
  | 'email'
  | 'birthdate'
  | 'cellphone'
  | 'city'
  | 'neighborhood'
  | 'creator';

const perPage = 20;

export default function Voter() {
  const navigate = useNavigate();
  const toast = useToast();
  const { office, role } = useAuth();
  const cancelRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const {
    isOpen: isOpenAlert,
    onOpen: onOpenAlert,
    onClose: onCloseAlert,
  } = useDisclosure();
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [data, setData] = useState<VoterDTO[]>([]);
  const [voterToDeleteId, setVoterToDeleteId] = useState('');
  const [selectFilter, setSelectFilter] = useState<VoterFilterType>('name');
  const [filterField, setFilterField] = useState('');
  const [filterFieldDateMask, setFilterFieldDateMask] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const debouncedValue = useDebounce(filterFieldDateMask || filterField, 500);

  const canManage = role?.eleitor_page > 1;
  const canCreateDemand = role?.demandas_page > 1;

  const getVoterList = useCallback(async (currentPage = 1) => {
    setLoading(true);

    try {
      const filterMapping = {
        creator: 'creator.name',
        city: 'city',
        neighborhood: 'neighborhood',
      };

      const currentFilter =
        filterMapping[selectFilter as keyof typeof filterMapping] || selectFilter;

      const response = await api.get(`/voter/office/${office.id}`, {
        params: {
          page: currentPage,
          quantity: perPage,
          field: currentFilter,
          value: filterFieldDateMask
            ? convertDateFormat(filterFieldDateMask)
            : filterField,
        },
      });

      setData(response.data.items);
      setTotalPages(response.data.total);
    } catch (error) {
      console.error('Failed to load voters', error);
    } finally {
      setLoading(false);
    }
  }, [filterField, filterFieldDateMask, office.id, selectFilter]);

  const getAllVoters = async () => {
    setExportLoading(true);

    try {
      const filterMapping = {
        creator: 'creator.name',
        city: 'city',
        neighborhood: 'neighborhood',
      };

      const currentFilter =
        filterMapping[selectFilter as keyof typeof filterMapping] || selectFilter;

      const response = await api.get(`/voter/office/${office.id}`, {
        params: {
          page: 1,
          quantity: 1000000,
          field: currentFilter,
          value: filterFieldDateMask
            ? convertDateFormat(filterFieldDateMask)
            : filterField,
        },
      });

      return response.data.items;
    } catch (error) {
      toast({
        title: 'Erro ao exportar dados',
        description: 'Nao foi possivel buscar os apoiadores para exportacao.',
        status: 'error',
        position: 'top-right',
        duration: 3000,
        isClosable: true,
      });
      return [];
    } finally {
      setExportLoading(false);
    }
  };

  const downloadExcel = async () => {
    const voters = await getAllVoters();
    if (voters.length === 0) return;

    const excelData = voters.map((voter: VoterDTO) => ({
      Nome: voter.name || '',
      Referencia: voter.reference || '',
      Telefone: voter.cellphone || '',
      Nascimento: voter.birthdate
        ? getFormatDate(new Date(voter.birthdate), 'dd/MM/yyyy')
        : '',
      Email: voter.email || '',
      Genero:
        voter.gender === 'MALE'
          ? 'Masculino'
          : voter.gender === 'FEMALE'
          ? 'Feminino'
          : '',
      Rua: voter.street || '',
      Numero: voter.address_number || '',
      Bairro: voter.neighborhood || '',
      Complemento: voter.complement || '',
      Cidade: voter.city || '',
      Estado: voter.state || '',
      CEP: voter.zip || '',
    }));

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Apoiadores');

    XLSX.writeFile(
      workbook,
      `apoiadores-${office?.name}-${new Date().toISOString().split('T')[0]}.xlsx`,
    );
  };

  const downloadPdf = async () => {
    const voters = await getAllVoters();
    if (voters.length === 0) return;

    const blob = await pdf(<MyDocument voters={voters} />).toBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `apoiadores-${office?.name}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDateOfBirthChange = (input: string) => {
    const dateRegEx = /^(\d{1,2})(\/)?(\d{1,2})?(\d{0,4})?$/;
    const match = input.match(dateRegEx);

    if (match) {
      let formattedDate = match[1];

      if (match[3]) formattedDate += `/${match[3]}`;
      if (match[4]) formattedDate += `/${match[4]}`;

      setFilterFieldDateMask(formattedDate);
    } else {
      setFilterFieldDateMask(input);
    }

    setFilterField(input);
  };

  useEffect(() => {
    if (office?.id) {
      getVoterList(page);
    }
  }, [debouncedValue, getVoterList, office?.id, page]);

  useEffect(() => {
    setPage(1);
  }, [debouncedValue]);

  useEffect(() => {
    setFilterField('');
    setFilterFieldDateMask('');
  }, [selectFilter]);

  const deleteVoter = async () => {
    setLoading(true);

    try {
      await api.delete(`/voter/${voterToDeleteId}`);

      toast({
        title: 'Apoiador excluido com sucesso',
        status: 'success',
        position: 'top-right',
        duration: 3000,
        isClosable: true,
      });
      setVoterToDeleteId('');
      getVoterList(page);
    } catch (err: any) {
      toast({
        title:
          err?.response?.data?.message ||
          'Ocorreu um erro ao excluir o apoiador, tente novamente',
        status: 'error',
        position: 'top-right',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
      onCloseAlert();
    }
  };

  const metrics = useMemo(() => {
    const withPhone = data.filter(voter => voter.cellphone).length;
    const withEmail = data.filter(voter => voter.email).length;
    const withAddress = data.filter(voter => voter.city || voter.neighborhood || voter.street).length;
    const totalTasks = data.reduce((acc, voter) => acc + (voter.tasks?.length || 0), 0);

    return {
      showing: data.length,
      withPhone,
      withEmail,
      withAddress,
      totalTasks,
    };
  }, [data]);

  const styles = StyleSheet.create({
    page: {
      fontSize: 11,
      flexDirection: 'row',
      justifyContent: 'center',
      padding: 10,
    },
    table: {
      width: '90%',
      flexDirection: 'column',
    },
    flexBetween: {
      width: '100%',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    tableTitle: {
      flexDirection: 'row',
      justifyContent: 'center',
      textAlign: 'center',
    },
    tableSubTitle: {
      flexDirection: 'row',
      textAlign: 'center',
      padding: 8,
    },
    tableContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 8,
      borderTop: '1px solid black',
      borderRight: '1px solid black',
      borderLeft: '1px solid black',
    },
    rowTitle: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 8,
      fontWeight: 500,
      borderTop: '1px solid black',
      borderRight: '1px solid black',
      borderLeft: '1px solid black',
    },
    finalRow: {
      flexDirection: 'row',
      alignItems: 'center',
      fontWeight: 'bold',
      padding: 8,
      border: '1px solid black',
    },
    voter: {
      width: '40%',
      padding: 1,
    },
    reference: {
      width: '40%',
      padding: 1,
    },
    cellphone: {
      width: '20%',
      padding: 1,
    },
    image: {
      width: 25,
      height: 25,
      alignSelf: 'center',
    },
  });

  const MyDocument = ({ voters }: { voters: VoterDTO[] }) => (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.table}>
          <View style={styles.flexBetween}>
            {office?.logo_url && <Image style={styles.image} src={office.logo_url} />}
            <TextPDF style={styles.tableTitle}>{office?.name}</TextPDF>
          </View>
          <TextPDF style={styles.tableSubTitle}>Lista de apoiadores</TextPDF>
          <View style={styles.tableContainer}>
            <View style={styles.rowTitle}>
              <TextPDF style={styles.voter}>Apoiador</TextPDF>
              <TextPDF style={styles.reference}>Referencia</TextPDF>
              <TextPDF style={styles.cellphone}>Telefone</TextPDF>
            </View>

            {voters.map(pageItem => (
              <View
                key={pageItem.id}
                style={pageItem.id === voters[voters.length - 1]?.id ? styles.finalRow : styles.row}
              >
                <TextPDF style={styles.voter}>{pageItem.name}</TextPDF>
                <TextPDF style={styles.reference}>{pageItem.reference}</TextPDF>
                <TextPDF style={styles.cellphone}>{pageItem.cellphone}</TextPDF>
              </View>
            ))}
          </View>
        </View>
      </Page>
    </Document>
  );

  return (
    <HeaderSideBar>
      <AlertDialog
        leastDestructiveRef={cancelRef}
        isOpen={isOpenAlert}
        onClose={onCloseAlert}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent mx="12px">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Deseja excluir este apoiador?
            </AlertDialogHeader>
            <AlertDialogBody>
              Essa acao e irreversivel. O cadastro e o historico vinculado podem ser perdidos.
            </AlertDialogBody>
            <AlertDialogFooter>
              <ChakraButton onClick={onCloseAlert}>Cancelar</ChakraButton>
              <ChakraButton colorScheme="red" ml={3} isLoading={loading} onClick={deleteVoter}>
                Excluir
              </ChakraButton>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Flex direction="column" gap="16px" pb="24px">
        <Box
          borderRadius="24px"
          px={{ base: '20px', md: '28px' }}
          py={{ base: '22px', md: '28px' }}
          bgGradient={`linear(135deg, ${office?.primary_color || '#0F766E'} 0%, ${
            office?.secondary_color || '#0F172A'
          } 100%)`}
          color="white"
          boxShadow="0 24px 80px rgba(15, 23, 42, 0.18)"
        >
          <Flex justify="space-between" gap="16px" direction={{ base: 'column', lg: 'row' }}>
            <Box maxW="720px">
              <Badge
                bg="rgba(255,255,255,0.14)"
                color="white"
                borderRadius="full"
                px="12px"
                py="4px"
                mb="12px"
              >
                Base politica
              </Badge>
              <Text fontSize={{ base: '24px', md: '32px' }} fontWeight="700">
                Apoiadores e relacionamento territorial
                {loading && <Spinner color="white" ml="3" size="sm" />}
              </Text>
              <Text mt="8px" color="rgba(255,255,255,0.82)" maxW="600px">
                Organize a base por contato, origem e historico de demandas para acelerar
                atendimento, mensageria e mobilizacao.
              </Text>
            </Box>

            <Flex gap="10px" direction={{ base: 'column', md: 'row' }}>
              {canManage && (
                <Button onClick={() => navigate('/eleitor/registrar-eleitor')} w={{ base: '100%', md: '230px' }}>
                  Cadastrar apoiador
                </Button>
              )}
            </Flex>
          </Flex>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2, xl: 5 }} spacing="16px">
          {[
            { label: 'Em exibicao', value: metrics.showing, icon: FiUsers },
            { label: 'Com telefone', value: metrics.withPhone, icon: IoLogoWhatsapp },
            { label: 'Com e-mail', value: metrics.withEmail, icon: FiMail },
            { label: 'Com endereco', value: metrics.withAddress, icon: FiMapPin },
            { label: 'Demandas visiveis', value: metrics.totalTasks, icon: MdOutlineTaskAlt },
          ].map(item => (
            <Box
              key={item.label}
              bg="white"
              borderRadius="20px"
              p="18px"
              border="1px solid #E2E8F0"
              boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
            >
              <Flex justify="space-between" align="center" mb="14px">
                <Text fontSize="13px" color="#64748B" fontWeight="600">
                  {item.label}
                </Text>
                <Icon as={item.icon} fontSize="18px" color="#0F766E" />
              </Flex>
              <Text fontSize="34px" lineHeight="1" fontWeight="700" color="#0F172A">
                {item.value}
              </Text>
            </Box>
          ))}
        </SimpleGrid>

        <Box
          bg="white"
          borderRadius="24px"
          p={{ base: '18px', md: '22px' }}
          border="1px solid #E2E8F0"
          boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
        >
          <Flex
            justify="space-between"
            gap="12px"
            direction={{ base: 'column', xl: 'row' }}
            align={{ base: 'stretch', xl: 'center' }}
          >
            <Box>
              <Text fontSize="20px" fontWeight="700" color="#0F172A">
                Operacao da base
              </Text>
              <Text fontSize="13px" color="#64748B">
                Filtre a listagem por pessoa, territorio ou dados de contato e exporte a base.
              </Text>
            </Box>

            <Flex gap="10px" direction={{ base: 'column', md: 'row' }}>
              <Select
                value={selectFilter}
                onChange={event => setSelectFilter(event.target.value as VoterFilterType)}
                w={{ base: '100%', md: '180px' }}
                borderColor="#CBD5E1"
                bg="#F8FAFC"
              >
                <option value="all">Todos</option>
                <option value="name">Nome</option>
                <option value="reference">Referencia</option>
                <option value="email">E-mail</option>
                <option value="birthdate">Nascimento</option>
                <option value="cellphone">Telefone</option>
                <option value="city">Cidade</option>
                <option value="neighborhood">Bairro</option>
                <option value="creator">Criador</option>
              </Select>

              <Box position="relative">
                <Input
                  value={selectFilter === 'birthdate' ? filterFieldDateMask : filterField}
                  onChange={event =>
                    selectFilter === 'birthdate'
                      ? handleDateOfBirthChange(event.target.value)
                      : setFilterField(event.target.value)
                  }
                  placeholder={selectFilter === 'birthdate' ? 'dd/mm/aaaa' : 'Buscar apoiador'}
                  pl="38px"
                  w={{ base: '100%', md: '260px' }}
                  borderColor="#CBD5E1"
                  bg="#F8FAFC"
                />
                <Icon
                  as={IoSearchSharp}
                  position="absolute"
                  left="12px"
                  top="50%"
                  transform="translateY(-50%)"
                  color="#64748B"
                />
              </Box>

              <Button
                w={{ base: '100%', md: '140px' }}
                leftIcon={<Icon as={IoPrintOutline} fontSize="18px" />}
                onClick={downloadPdf}
                isLoading={exportLoading}
              >
                Imprimir
              </Button>

              <Button
                w={{ base: '100%', md: '140px' }}
                leftIcon={<Icon as={IoDownloadOutline} fontSize="18px" />}
                onClick={downloadExcel}
                isLoading={exportLoading}
              >
                Exportar
              </Button>
            </Flex>
          </Flex>
        </Box>

        <Box
          bg="white"
          borderRadius="24px"
          border="1px solid #E2E8F0"
          boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
          overflow="hidden"
        >
          <Box overflow="auto">
            <Table>
              <Thead bg="#F8FAFC">
                <Tr>
                  {canCreateDemand && <Th color="#475569">Demanda</Th>}
                  <Th color="#475569">Nome</Th>
                  <Th color="#475569">Referencia</Th>
                  <Th color="#475569">Contato</Th>
                  <Th color="#475569">Nascimento</Th>
                  <Th color="#475569">Criador</Th>
                  <Th color="#475569">Demandas</Th>
                  <Th color="#475569">Territorio</Th>
                  {canManage && <Th color="#475569">Acoes</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {data.length > 0 ? (
                  data.map(voter => (
                    <Tr key={voter.id}>
                      {canCreateDemand && (
                        <Td borderColor="#E2E8F0">
                          <IconButton
                            aria-label="Criar demanda"
                            borderRadius="14px"
                            bg="#EFF6FF"
                            icon={<Icon as={IoAddCircleSharp} color="#2563EB" fontSize="20px" />}
                            onClick={() => navigate(`/demanda/registrar-demanda/${voter.cellphone}`)}
                          />
                        </Td>
                      )}
                      <Td borderColor="#E2E8F0">
                        <Text fontWeight="700" color="#0F172A">
                          {voter.name}
                        </Text>
                        <Text fontSize="12px" color="#64748B">
                          {voter.email || 'Sem e-mail'}
                        </Text>
                      </Td>
                      <Td borderColor="#E2E8F0" color="#334155">
                        {voter.reference || '-'}
                      </Td>
                      <Td borderColor="#E2E8F0">
                        {voter.cellphone ? (
                          <Flex align="center" gap="8px">
                            <a
                              href={`https://wa.me/55${voter.cellphone}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Icon as={IoLogoWhatsapp} color="#16A34A" fontSize="18px" />
                            </a>
                            <Text color="#334155">{voter.cellphone}</Text>
                          </Flex>
                        ) : (
                          <Text color="#94A3B8">-</Text>
                        )}
                      </Td>
                      <Td borderColor="#E2E8F0" color="#334155">
                        {voter.birthdate
                          ? getFormatDate(new Date(voter.birthdate), 'dd/MM/yyyy')
                          : '-'}
                      </Td>
                      <Td borderColor="#E2E8F0" color="#334155">
                        {voter.creator?.name || '-'}
                      </Td>
                      <Td borderColor="#E2E8F0">
                        <Badge bg="#EFF6FF" color="#1D4ED8" borderRadius="full" px="10px" py="4px">
                          {voter.tasks?.length || 0}
                        </Badge>
                      </Td>
                      <Td borderColor="#E2E8F0">
                        <Text color="#334155">
                          {[voter.city, voter.neighborhood].filter(Boolean).join(' • ') || '-'}
                        </Text>
                        <Text fontSize="12px" color="#94A3B8">
                          {voter.street || ''}
                        </Text>
                      </Td>
                      {canManage && (
                        <Td borderColor="#E2E8F0">
                          <HStack spacing="6px">
                            <IconButton
                              aria-label="Editar apoiador"
                              borderRadius="14px"
                              bg="#F8FAFC"
                              icon={<Icon as={IoPencilOutline} color="#334155" fontSize="18px" />}
                              onClick={() => navigate(`/eleitor/${voter.id}`, { state: { voter } })}
                            />
                            <IconButton
                              aria-label="Excluir apoiador"
                              borderRadius="14px"
                              bg="#FFF1F2"
                              icon={<Icon as={IoTrashOutline} color="#BE123C" fontSize="18px" />}
                              onClick={() => {
                                setVoterToDeleteId(voter.id);
                                onOpenAlert();
                              }}
                            />
                          </HStack>
                        </Td>
                      )}
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td
                      colSpan={canManage ? (canCreateDemand ? 9 : 8) : canCreateDemand ? 8 : 7}
                      py="24px"
                      textAlign="center"
                      color="#64748B"
                    >
                      Nenhum apoiador encontrado para os filtros atuais.
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        </Box>

        <Flex alignItems="center" justifyContent="center">
          <Pagination
            currentPage={page}
            perPage={perPage}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </Flex>
      </Flex>
    </HeaderSideBar>
  );
}
